import { AccessTypeEnum } from '@api/ringil-api.v2.tsx';
import type { DAPSupplierNotificationInDTO } from '@api/ringil-api.v2.tsx';
import type { SupplierNotificationDapForm } from '@app/pages/dap/CreateSupplierNotificationDap.tsx';

/**
 * RD-3062 — the form → API mapping of `CreateSupplierNotificationDap`, moved out of the component unchanged so
 * it can be unit-tested. The component keeps the hooks, API call and UI; this is pure.
 */

/**
 * The body of `createDAPSupplierNotification`. `values` are the live form values — the labels have always
 * been read from there rather than from the submitted `data`.
 */
export const buildDapSupplierNotificationRequest = (data: SupplierNotificationDapForm, values: SupplierNotificationDapForm): DAPSupplierNotificationInDTO =>
  ({
    companyId: data.companyId,
    supplierId: data.supplierId,
    attachments: data.attachments.map(a => ({
      attachmentId: a.attachmentId,
      documentType: a.documentType,
      accessType: a.accessType || AccessTypeEnum.ALL,
      labelIds: a.labels?.map(l => l.labelId) ?? [],
      userDateLocalDate: a.userDateLocalDate ?? null,
      userNote : a.userNote,
    })),
    responsiblePersonId: data.notifyData?.responsiblePerson?.id,
    userIdsToNotify: data.notifyData?.userToNotify?.map(u => u.id),
    notifyUsersDependsOnRole: data.notifyData?.notifyUsersDependsOnRole,
    transportationType: data.transportationType,
    carrierName: data.carrierName,
    shipmentNumber: data.shipmentNumber,
    orders: data.orders.map(o => ({
      orderName: o.orderName,
      items: o.items.map(i => ({
        amount: i.amount,
        cargoTemplateCode: i.cargoTemplateCode,
        width: i.width,
        height: i.height,
        length: i.length,
        weight: i.weight,
        note: i.itemNote,
        stackable: i.stackable,
      })),
      orderAmount: o.orderAmount,
      orderDescription: o.orderDescription,
      orderValue: o.orderValue,
    })),
    supplierLocationId: data.supplierLocationId,
    customerLocationId: data.customerLocationId,
    internalInvoiceNumber: data.goodsInvoice?.goodsInvoiceNumber,
    invoiceDueDate: data.goodsInvoice?.goodsInvoiceDate,
    internalNote: data.internalNote,
    loadingGlobalTimeslots: data.loadingGlobalTimeslots,
    unloadingGlobalTimeslots: data.unloadingGlobalTimeslots,
    labelIds: values.labels?.map(t => t.labelId),
    folderIds: (data.folders ?? []).map(f => f.id),
  } as DAPSupplierNotificationInDTO);
